import { useContext } from 'react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import config from 'src/config';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import type { FamousOrderConfirmationDTO } from '@erp/rpc';
import type useSendEdi855Transaction from 'src/api/temp/famous/useSendEdi855Transaction';
import { T, Var, useGT } from 'gt-react';

export interface SendEdi855ButtonProps {
  orderConfirmation: FamousOrderConfirmationDTO;
  sendEdi855Transaction: ReturnType<typeof useSendEdi855Transaction>;
  onSendEdi: (force?: boolean) => void;
  onRefreshStatus: () => void;
  isLoading: boolean;
}

function SendEdi855Button({
  orderConfirmation,
  sendEdi855Transaction,
  onSendEdi,
  onRefreshStatus,
  isLoading,
}: SendEdi855ButtonProps) {
  const gt = useGT();
  const sentAt = orderConfirmation.edi855SentAt;
  const statusRaw = orderConfirmation.edi855Status;
  const isError = statusRaw === 'error';
  const isPending = statusRaw === 'pending';
  const isSent = statusRaw === 'sent';
  const transactionId = orderConfirmation.edi855TransactionId;
  const errorMessage = orderConfirmation.edi855Error;
  const isDevelopment = config.isDev;
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const userIsAdmin = isAdmin(currentUser, viewMode);

  function renderSentStatusUi() {
    return (
      <MDBox>
        <MDBox
          sx={{
            p: 2,
            backgroundColor: '#e8f5e8',
            borderRadius: 1,
            border: '1px solid #c8e6c9',
            mb: 2,
          }}
        >
          <MDTypography
            variant="body2"
            fontWeight="bold"
            color="success.main"
            mb={1}
          >
            <T>EDI 855 Sent</T>
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            <T>
              Sent:{' '}
              <Var>{sentAt ? new Date(sentAt).toLocaleDateString() : ''}</Var>{' '}
              at{' '}
              <Var>
                {sentAt
                  ? new Date(sentAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : ''}
              </Var>
            </T>
          </MDTypography>
          {transactionId && (
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              <T>
                Transaction ID: <Var>{transactionId}</Var>
              </T>
            </MDTypography>
          )}
        </MDBox>

        {transactionId && (
          <MDButton
            variant="outlined"
            color="primary"
            onClick={() =>
              window.open(
                `https://ui.orderful.com/transactions/${transactionId}`,
                '_blank',
                'noopener,noreferrer',
              )
            }
            fullWidth
            sx={{ mb: 2 }}
          >
            <T>View in Orderful</T>
          </MDButton>
        )}
      </MDBox>
    );
  }

  function renderErrorStatusUi() {
    return (
      <MDBox
        sx={{
          p: 2,
          backgroundColor: '#ffebee',
          borderRadius: 1,
          border: '1px solid #ffcdd2',
          mb: 2,
        }}
      >
        <MDTypography
          variant="body2"
          fontWeight="bold"
          color="error.main"
          mb={1}
        >
          <T>EDI 855 Failed</T>
        </MDTypography>
        {sentAt && (
          <MDTypography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            <T>
              Last attempt: <Var>{new Date(sentAt).toLocaleDateString()}</Var>{' '}
              at{' '}
              <Var>
                {new Date(sentAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Var>
            </T>
          </MDTypography>
        )}
        {errorMessage && (
          <MDTypography
            variant="caption"
            color="error"
            sx={{ display: 'block' }}
          >
            {errorMessage}
          </MDTypography>
        )}
      </MDBox>
    );
  }

  function renderPendingStatusUi() {
    return (
      <MDBox>
        <MDBox
          sx={{
            p: 2,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #90caf9',
            mb: 2,
          }}
        >
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <MDTypography variant="body2" fontWeight="bold" color="info.main">
              <T>EDI 855 Pending</T>
            </MDTypography>
            <MDButton
              variant="text"
              color="dark"
              onClick={onRefreshStatus}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <ArrowsClockwiseIcon size={18} />
            </MDButton>
          </MDBox>
          {sentAt && (
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              <T>
                Submitted: <Var>{new Date(sentAt).toLocaleDateString()}</Var> at{' '}
                <Var>
                  {new Date(sentAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Var>
              </T>
            </MDTypography>
          )}
          {transactionId && (
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              <T>
                Transaction ID: <Var>{transactionId}</Var>
              </T>
            </MDTypography>
          )}
        </MDBox>

        {transactionId && (
          <MDButton
            variant="outlined"
            color="info"
            onClick={() =>
              window.open(
                `https://ui.orderful.com/transactions/${transactionId}`,
                '_blank',
                'noopener,noreferrer',
              )
            }
            fullWidth
            sx={{ mb: 2 }}
          >
            <T>View in Orderful</T>
          </MDButton>
        )}
      </MDBox>
    );
  }

  function renderSendButtonUi(forceResend = false) {
    return (
      <MDBox>
        <MDButton
          variant="contained"
          color={forceResend ? 'warning' : 'primary'}
          onClick={() => onSendEdi(forceResend)}
          disabled={sendEdi855Transaction.isPending || isLoading}
          fullWidth
          sx={{ mb: 2 }}
        >
          {sendEdi855Transaction.isPending
            ? gt('Sending...', {
                context: 'button label while EDI 855 transaction is being sent',
              })
            : forceResend
              ? gt('Resend EDI 855 Transaction', {
                  context:
                    'button label to force-resend an EDI 855 transaction',
                })
              : isError
                ? gt('Retry EDI 855 Transaction', {
                    context:
                      'button label to retry a failed EDI 855 transaction',
                  })
                : gt('Send EDI 855 Transaction', {
                    context: 'button label to send an EDI 855 transaction',
                  })}
        </MDButton>

        {sendEdi855Transaction.error && (
          <MDBox
            sx={{
              p: 2,
              backgroundColor: '#ffebee',
              borderRadius: 1,
              border: '1px solid #ffcdd2',
            }}
          >
            <MDTypography variant="caption" color="error">
              <T>
                Error:{' '}
                <Var>
                  {sendEdi855Transaction.error?.message ||
                    gt('Failed to send EDI 855 transaction')}
                </Var>
              </T>
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    );
  }

  function renderDevelopmentUi() {
    if (isSent) {
      // Match the production admin gate — the backend rejects `force=true`
      // from non-admins with FORBIDDEN, so showing the button to a
      // non-admin dev user would just produce a broken click.
      if (!userIsAdmin) {
        return renderSentStatusUi();
      }
      return (
        <MDBox>
          {renderSentStatusUi()}
          <AlertContainer className="mb-2 border-amber-200 bg-amber-50">
            <AlertDescription className="text-amber-800">
              <T>
                Development mode: You can resend the EDI 855 transaction. This
                will create a new transaction in Orderful.
              </T>
            </AlertDescription>
          </AlertContainer>
          {renderSendButtonUi(true)}
        </MDBox>
      );
    }
    if (isPending) return renderPendingStatusUi();
    if (isError) {
      return (
        <MDBox>
          {renderErrorStatusUi()}
          {renderSendButtonUi(false)}
        </MDBox>
      );
    }
    return renderSendButtonUi(false);
  }

  function renderProductionUi() {
    if (isSent) {
      // Admins can force-resend a 855 that appears "sent" but actually
      // failed downstream (e.g. Orderful accepted the POST but delivery
      // was rejected). Non-admins only see the sent status.
      if (userIsAdmin) {
        return (
          <MDBox>
            {renderSentStatusUi()}
            <AlertContainer className="mb-2 border-amber-200 bg-amber-50">
              <AlertDescription className="text-amber-800">
                <T>
                  Admin: this will create a new transaction in Orderful. Only
                  use this if the 855 was not actually delivered.
                </T>
              </AlertDescription>
            </AlertContainer>
            {renderSendButtonUi(true)}
          </MDBox>
        );
      }
      return renderSentStatusUi();
    }
    if (isPending) return renderPendingStatusUi();
    if (isError) {
      return (
        <MDBox>
          {renderErrorStatusUi()}
          {renderSendButtonUi(false)}
        </MDBox>
      );
    }
    return renderSendButtonUi();
  }

  return isDevelopment ? renderDevelopmentUi() : renderProductionUi();
}

export default SendEdi855Button;
